import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import publicCouponApi from '../../../../services/publicCouponApi';
import { cn } from '../../../../components/ui/_cn';

// Handoff §3: the live check waits ~300 ms after the code settles.
export const CODE_CHECK_DELAY_MS = 300;

// CodeAvailability for one code (spec §1.2). The answer is the api's,
// keyed by the code it was asked for, or null when the check could not be
// made (network, 5xx): never a false "taken" (Review Focus 5) — the
// Upsert refusal on save stays authoritative. `check(code)` asks at once
// (the modals call it after a duplicate refusal, for the link).
export const useCodeCheck = ({ companyId, excludeId = null, code, enabled }) => {
  const [answer, setAnswer] = useState(null);
  const request = useRef(0);

  const check = useCallback(
    async (value) => {
      const id = ++request.current;
      try {
        const res = await publicCouponApi.codeAvailability({ companyid: companyId, code: value, excludeid: excludeId });
        const data = res?.data?.data;
        const next =
          data && typeof data.available === 'boolean'
            ? {
                code: value,
                available: data.available,
                message: data.message || null,
                usedby: data.usedby || null,
                suggestion: data.suggestion || null,
              }
            : null;
        if (id !== request.current) return null;
        setAnswer(next);
        return next;
      } catch {
        if (id === request.current) setAnswer(null);
        return null;
      }
    },
    [companyId, excludeId],
  );

  useEffect(() => {
    if (!enabled || !code) {
      request.current += 1;
      setAnswer(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      check(code);
    }, CODE_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [code, enabled, check]);

  return { answer: answer && answer.code === code ? answer : null, check };
};

// Handoff §3 "Editing the code": read-only (#FAFBFD fin-recess, monospace
// 600, 220px) with a pencil; the pencil turns it into an input with the
// code selected; Enter or blur confirms, Esc cancels. The field keeps what
// was typed (lowercase only this way). A taken code reads the api's
// sentence with the coupon's name as a link to it.
const CodeField = ({ value, onCommit, error = null, owner = null, onOpenOwner, disabled = false, label = 'Coupon code' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  const settled = useRef(false);
  const errorId = useId();

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const start = () => {
    settled.current = false;
    setDraft(value);
    setEditing(true);
  };
  const commit = () => {
    if (settled.current) return;
    settled.current = true;
    setEditing(false);
    const next = draft.trim();
    if (next !== value) onCommit(next);
  };
  const cancel = () => {
    settled.current = true;
    setEditing(false);
    setDraft(value);
  };

  const invalid = Boolean(error || owner);
  const describedBy = invalid ? errorId : undefined;

  return (
    <div className="space-y-1.5">
      <p className="text-[13px] font-semibold text-fin-ink">{label}</p>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          aria-label={label}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          spellCheck={false}
          autoComplete="off"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            // preventDefault: the modal's Dialog closes on an Escape nobody
            // handled (headlessui reads defaultPrevented).
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
          className={cn(
            'h-10 w-full max-w-[220px] rounded-lg border bg-fin-surface px-3 font-mono text-[13.5px] font-semibold text-fin-ink outline-none',
            invalid ? 'border-fin-red' : 'border-fin-blue',
          )}
        />
      ) : (
        <div
          aria-describedby={describedBy}
          className={cn(
            'flex h-10 w-full max-w-[220px] items-center justify-between gap-2 rounded-lg border bg-fin-recess pl-3 pr-1',
            invalid ? 'border-fin-red' : 'border-fin-border',
          )}
        >
          <span className="truncate font-mono text-[13.5px] font-semibold text-fin-ink">{value}</span>
          {disabled ? null : (
            <button
              type="button"
              aria-label="Edit coupon code"
              onClick={start}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-fin-muted hover:bg-fin-neutral-pale hover:text-fin-ink"
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      {owner ? (
        <p id={errorId} className="text-[12.5px] text-fin-red">
          {'This code is already used by “'}
          <button type="button" onClick={() => onOpenOwner(owner)} className="font-semibold underline">
            {owner.name}
          </button>
          {'”. Choose another one.'}
        </p>
      ) : error ? (
        <p id={errorId} className="text-[12.5px] text-fin-red">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default CodeField;
